import { JSX, useEffect, useMemo, useState } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import { useAuth } from "../../context/AuthContext";
import { profileService } from "../../services/profileService";
import { toast } from "react-hot-toast";

// Sovereign Emerald palette
const SE = {
  paper:         '#FAF7F0',
  raised:        'rgba(255,253,246,0.97)',
  hairline:      'rgba(11,59,46,0.14)',
  hairlineSoft:  'rgba(11,59,46,0.10)',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  champagneRing: 'rgba(201,169,97,0.45)',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
};

const EditPencil = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const FieldRow = ({
  icon, label, value, fullWidth,
}: {
  icon: JSX.Element;
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <div
    className={`flex items-start gap-3 rounded-xl p-3 transition-all duration-150 ${fullWidth ? "lg:col-span-2" : ""}`}
    style={{ backgroundColor: SE.raised, border: `1px solid ${SE.hairline}` }}
    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = SE.champagneRing; }}
    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = SE.hairline; }}
  >
    <div
      className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
      style={{ backgroundColor: SE.champagneTile, color: SE.bottle, boxShadow: `inset 0 0 0 1px ${SE.champagneRing}` }}
    >
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: SE.muted }}>
        {label}
      </p>
      <p className="mt-0.5 break-words text-[13px] font-medium" style={{ color: SE.ink }}>
        {value || <span className="italic" style={{ color: SE.muted }}>Not provided</span>}
      </p>
    </div>
  </div>
);

/* Field icons */
const HomeIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
  </svg>
);
const ApartmentIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-13h2m-2 4h2m4-4h2m-2 4h2m-6 4h6" />
  </svg>
);
const PinIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 13a3 3 0 100-6 3 3 0 000 6z" />
  </svg>
);
const PhoneIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5a2 2 0 012-2h2.6a1 1 0 01.95.68l1.2 3.6a1 1 0 01-.27 1.05l-1.6 1.6a14 14 0 006.4 6.4l1.6-1.6a1 1 0 011.05-.27l3.6 1.2a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.4 21 3 14.6 3 6V5z" />
  </svg>
);

const PINCODE_RE = /^\d{6}$/;
const PHONE_RE   = /^[+]?[\d\s-]{7,15}$/;

function readEmergency(addr: unknown): string {
  if (addr && typeof addr === "object" && "emergencyNumber" in addr) {
    return String((addr as { emergencyNumber?: string }).emergencyNumber ?? "");
  }
  return "";
}

export default function UserAddressCard() {
  const { isOpen, openModal, closeModal } = useModal();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);

  const buildFormData = () => ({
    country: user?.address?.country || user?.country || "",
    city:    user?.address?.city    || user?.city    || "",
    state:   user?.address?.state   || user?.state   || "",
    taxId:   user?.taxId || user?.taxNumber || "",
    street:      user?.address?.street      || user?.street      || "",
    apartment:   user?.address?.apartment   || user?.apartment   || "",
    postalCode:  user?.address?.postalCode  || user?.postalCode  || user?.zipCode || "",
    emergencyNumber: readEmergency(user?.address),
  });

  const [formData, setFormData] = useState(buildFormData());

  useEffect(() => {
    if (isOpen) setFormData(buildFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const pincodeError  = useMemo(() => { const v = formData.postalCode.trim(); if (!v) return null; return PINCODE_RE.test(v) ? null : "Enter a valid 6-digit pincode."; }, [formData.postalCode]);
  const emergencyError = useMemo(() => { const v = formData.emergencyNumber.trim(); if (!v) return null; return PHONE_RE.test(v) ? null : "Enter a valid phone number (7–15 digits)."; }, [formData.emergencyNumber]);
  const canSave = !saving && !pincodeError && !emergencyError;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const result = await profileService.updateProfile({
        address: {
          country: formData.country,
          city:    formData.city,
          state:   formData.state,
          taxId:   formData.taxId,
          street:         formData.street.trim(),
          apartment:      formData.apartment.trim(),
          postalCode:     formData.postalCode.trim(),
          emergencyNumber: formData.emergencyNumber.trim(),
        },
      });
      if (result.success) {
        toast.success("Address updated");
        if (result.data) {
          updateUser(result.data);
        } else {
          updateUser({
            address: {
              country: formData.country, city: formData.city, state: formData.state,
              street: formData.street.trim(), apartment: formData.apartment.trim(),
              postalCode: formData.postalCode.trim(), emergencyNumber: formData.emergencyNumber.trim(),
            },
            taxId: formData.taxId,
          });
        }
        closeModal();
      } else { toast.error(result.message); }
    } catch { toast.error("Failed to update address"); }
    finally { setSaving(false); }
  };

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const userStreet     = user?.address?.street    || user?.street    || "";
  const userApartment  = user?.address?.apartment || user?.apartment || "";
  const userPostalCode = user?.address?.postalCode || user?.postalCode || user?.zipCode || "";
  const userEmergency  = readEmergency(user?.address);

  return (
    <>
      <div
        className="rounded-2xl p-5 lg:p-6"
        style={{
          backgroundColor: SE.paper,
          border: `1px solid ${SE.hairline}`,
          boxShadow: '1px 0 0 rgba(201,169,97,0.06), 0 4px 16px rgba(11,59,46,0.06)',
        }}
      >
        {/* ── SECTION HEADER ─────────────────────────────────────────── */}
        <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm"
              style={{ backgroundImage: `linear-gradient(135deg, ${SE.bottle}, ${SE.title})` }}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-[15px] font-bold tracking-tight" style={{ color: SE.title }}>
                Address & Emergency Contact
              </h4>
              <p className="text-[11px] mt-0.5" style={{ color: SE.muted }}>
                Where you're based and who to reach in an emergency
              </p>
            </div>
          </div>
          <button
            onClick={openModal}
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold transition-all duration-200"
            style={{ backgroundColor: SE.champagneWash, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneTile;
              (e.currentTarget as HTMLElement).style.boxShadow = `0 2px 8px rgba(201,169,97,0.20)`;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash;
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
            }}
          >
            <EditPencil />
            Edit
          </button>
        </div>

        {/* Champagne divider */}
        <div className="mb-5 h-px" style={{ background: `linear-gradient(to right, transparent, ${SE.champagneRing}, transparent)` }} />

        {/* ── FIELDS ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <FieldRow label="Full Address"      icon={HomeIcon}      value={userStreet}      fullWidth />
          <FieldRow label="Apartment / Suite" icon={ApartmentIcon} value={userApartment} />
          <FieldRow label="Pincode"            icon={PinIcon}       value={userPostalCode} />
          <FieldRow label="Emergency Number"  icon={PhoneIcon}     value={userEmergency}   fullWidth />
        </div>
      </div>

      {/* ── EDIT MODAL ───────────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[640px] m-4">
        <div className="relative w-full p-4 overflow-y-auto bg-white no-scrollbar rounded-3xl dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              Edit Address & Emergency Contact
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 lg:mb-7">
              Update your address, pincode, apartment, and emergency contact number.
            </p>
          </div>
          <form className="flex flex-col" onSubmit={(e) => { e.preventDefault(); void handleSave(); }}>
            <div className="px-2 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                <div className="lg:col-span-2">
                  <Label>Full Address</Label>
                  <textarea
                    rows={2}
                    value={formData.street}
                    onChange={(e) => handleInputChange("street", e.target.value)}
                    placeholder="House / building, street, area, city, state, country"
                    className="dark:bg-dark-900 h-auto w-full resize-y rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#a3cd39] focus:outline-hidden focus:ring-3 focus:ring-[#a3cd39]/20 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-white/30"
                  />
                </div>
                <div>
                  <Label>Apartment / Suite</Label>
                  <Input type="text" value={formData.apartment} onChange={(e) => handleInputChange("apartment", e.target.value)} placeholder="Apartment, suite, unit (optional)" />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input type="text" value={formData.postalCode} onChange={(e) => handleInputChange("postalCode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))} placeholder="6-digit pincode" />
                  {pincodeError && <p className="mt-1 text-xs font-medium text-rose-600">{pincodeError}</p>}
                </div>
                <div className="lg:col-span-2">
                  <Label>Emergency Number</Label>
                  <Input type="tel" value={formData.emergencyNumber} onChange={(e) => handleInputChange("emergencyNumber", e.target.value)} placeholder="e.g. +91 98765 43210" />
                  {emergencyError
                    ? <p className="mt-1 text-xs font-medium text-rose-600">{emergencyError}</p>
                    : <p className="mt-1 text-xs text-gray-400">Whom should we contact in case of an emergency.</p>}
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3 px-2 lg:justify-end">
              <Button size="sm" variant="outline" onClick={closeModal} disabled={saving}>Close</Button>
              <Button size="sm" onClick={handleSave} disabled={!canSave} type="submit">{saving ? "Saving…" : "Save Changes"}</Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
